/**
 * Presets as data: the shape an authored preset takes, and the loader that reads one from
 * untrusted data (parsed JSON) and checks it against the validity matrix.
 *
 * A preset definition is plain data - numbers, words, lists and objects, never a function - so it
 * can be stored, sent and authored outside the code. It is a {@link GradingPreset} the engine runs
 * as is, plus the metadata the model asks for: its role, the surfaces it belongs to, who consumes
 * it, the material it grades and the advancement it is authored for.
 *
 * The two escape hatches (a verdict decided by a function, an accumulator updated by a function)
 * are code, so they are not part of the data shape. No catalog preset needs either.
 */

import {
  Band,
  Boundary,
  CombiningConfig,
  CombiningLevel,
  ItemInput,
  PileConfig,
  RecencyCurve,
  Scope,
  Statistic,
} from './combining';
import {
  CreditCurve,
  CurveLimit,
  CurvePoint,
  CurveSide,
  CurveSides,
  CurveUnit,
  LengthCurve,
  LengthPoint,
  LimitUnit,
  SegmentShape,
} from './creditCurve';
import { CutLines } from './cutLines';
import {
  Decoder,
  exactly,
  failAt,
  finiteNumber,
  flag,
  listOf,
  object,
  oneOf,
  optional,
  pairOf,
  partialRecordOf,
  recordOf,
  setOf,
  succeed,
  tagged,
  text,
} from './decode';
import { JudgmentConfig } from './grader';
import { ExtraNoteRule, MatchingConfig, MatchingRule } from './matching';
import { Combiner, DimensionGate, DimensionRule, NoteJudgmentConfig } from './noteJudgment';
import { PedalStreamConfig } from './pedal';
import { AccumulatorConfig, AccumulatorShape, ResetKind } from './policy';
import { GradingPreset, ScoringConfig } from './preset';
import { PresenceRule, RolledRule, SliceConfig, SliceRule } from './slices';
import { DIMENSIONS } from './types';
import {
  Comparison,
  CutLineTest,
  TallyCondition,
  TallyOperand,
  TallyRung,
  VerdictRule,
  VerdictView,
} from './verdict';

// ---------------------------------------------------------------------------------------------
// The shape.

/**
 * - `clearance`: produces an achievement record (and feeds the knowledge graph).
 * - `controller`: steers adaptation; records nothing.
 * - `feedback`: shown to the player; records nothing.
 */
export type PresetRole = 'clearance' | 'controller' | 'feedback';

export const PRESET_ROLES: readonly PresetRole[] = ['clearance', 'controller', 'feedback'];

/**
 * The learning surfaces, one list for presets and take records alike: the values the backend
 * stores for a session's surface.
 */
export type Surface = 'sight-reading' | 'repertoire' | 'exercise' | 'tutorial';

export const SURFACES: readonly Surface[] = ['sight-reading', 'repertoire', 'exercise', 'tutorial'];

/** Who reads a preset's results. Telemetry reads everything and is never listed. */
export type Consumer = 'achievement-record' | 'knowledge-graph' | 'adaptation' | 'display';

export const CONSUMERS: readonly Consumer[] = [
  'achievement-record',
  'knowledge-graph',
  'adaptation',
  'display',
];

/**
 * - `discrete`: a piece, a loop segment, an exercise - certified per take at one held tempo.
 * - `continuous`: an endless stream (sight reading) - certified over a moving window; tempo may
 *   drift live and generation is live.
 */
export type MaterialShape = 'discrete' | 'continuous';

export const MATERIAL_SHAPES: readonly MaterialShape[] = ['discrete', 'continuous'];

/** An axis that may move by itself in the middle of a unit (auto-tempo, live generation). */
export type LiveAxis = 'tempo' | 'complexity';

export const LIVE_AXES: readonly LiveAxis[] = ['tempo', 'complexity'];

/** On a mistake: wait on the slice until it is played right, or mark it and move on. */
export type OnMistake = 'hold' | 'move-on';

/** With timing judged: a steady clock at the set tempo, or a clock that follows the player. */
export type Pace = 'set-tempo' | 'follow-me';

/** The advancement a preset is authored for. */
export interface Advancement {
  readonly onMistake: OnMistake;
  /** Only when timing is judged (time-window matching); absent with timing off. */
  readonly pace?: Pace;
}

/** A verdict that is data: cut lines or a tally ladder, never a function. */
export type VerdictRuleData = Exclude<VerdictRule, { kind: 'rule' }>;

export interface ScoringData extends ScoringConfig {
  readonly verdict?: VerdictRuleData;
}

/** An accumulator shape that is data: every shape but the custom function. */
export type AccumulatorShapeData = Exclude<AccumulatorShape, { kind: 'custom' }>;

export interface AccumulatorData extends AccumulatorConfig {
  readonly shape: AccumulatorShapeData;
}

/** A named preset with its metadata. The engine runs it as a {@link GradingPreset}. */
export interface PresetDefinition extends GradingPreset {
  /** Stable key, lowercase words joined by hyphens (e.g. `take-ladder`). */
  readonly id: string;
  /** One line: what the preset does. */
  readonly description: string;
  readonly role: PresetRole;
  /** The surfaces the preset belongs to; an authored preset may belong to none. */
  readonly surfaces: readonly Surface[];
  /** Consumers beyond those the role implies (see {@link presetConsumers}). */
  readonly consumers: readonly Consumer[];
  readonly material: MaterialShape;
  /** Axes that may move by themselves mid-unit. Discrete material has none. */
  readonly liveAxes: readonly LiveAxis[];
  readonly advancement: Advancement;
  readonly scoring: ScoringData;
  readonly policy: Readonly<Record<string, AccumulatorData>>;
}

/**
 * A named pick at layers 0 to 1 only - which notes count and how each is judged - that full
 * presets can share (e.g. Pitch-Only, inside the untimed tutorial objective).
 */
export interface JudgmentPick {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly judgment: JudgmentConfig;
}

const IMPLIED_CONSUMERS: Readonly<Record<PresetRole, readonly Consumer[]>> = {
  clearance: ['achievement-record', 'knowledge-graph'],
  controller: ['adaptation'],
  feedback: ['display'],
};

/** Every consumer of a preset: those its role implies, then those it lists. */
export function presetConsumers(preset: PresetDefinition): Consumer[] {
  const implied = IMPLIED_CONSUMERS[preset.role];
  return [...implied, ...preset.consumers.filter((consumer) => implied.indexOf(consumer) < 0)];
}

// ---------------------------------------------------------------------------------------------
// Decoders for the engine's configuration types.

const curveUnit = oneOf<CurveUnit>([
  'ms',
  'beats',
  'proportion',
  'velocity',
  'semitones',
  'fingers',
]);

const curvePoint = object<CurvePoint>({
  distance: finiteNumber,
  credit: finiteNumber,
  shape: oneOf<SegmentShape>(['step', 'linear', 'curve']),
  exponent: optional(finiteNumber),
});

const segmentShape = oneOf<SegmentShape>(['step', 'linear', 'curve']);

const curveLimit = object<CurveLimit>({
  amount: finiteNumber,
  unit: oneOf<LimitUnit>(['beats', 'ms']),
});

const lengthCurve = object<LengthCurve>({
  from: oneOf<'target' | 'start'>(['target', 'start']),
  points: listOf(
    object<LengthPoint>({
      length: finiteNumber,
      value: finiteNumber,
      shape: segmentShape,
      exponent: optional(finiteNumber),
    }),
  ),
});

const curveSide = object<CurveSide>({
  unit: curveUnit,
  min: optional(curveLimit),
  max: optional(curveLimit),
  edgeByLength: optional(lengthCurve),
});

const creditCurve = object<CreditCurve>({
  unit: curveUnit,
  plateau: pairOf(finiteNumber),
  below: listOf(curvePoint),
  above: listOf(curvePoint),
  sides: optional(object<CurveSides>({ below: optional(curveSide), above: optional(curveSide) })),
});

const combiner = oneOf<Combiner>(['worst-of', 'product', 'weighted-average']);

const dimensionRule = object<DimensionRule>({
  curve: creditCurve,
  gate: optional(object<DimensionGate>({ passAt: finiteNumber })),
  weight: optional(finiteNumber),
});

const noteJudgment = object<NoteJudgmentConfig>({
  dimensions: partialRecordOf(DIMENSIONS, dimensionRule),
  combiner,
});

const matchingRule = oneOf<MatchingRule>(['time-window', 'order']);

const extraNotesNotCounted = object<Extract<ExtraNoteRule, { counts: false }>>({
  counts: exactly(false),
});

const extraNotesCharged = object<Extract<ExtraNoteRule, { charge: 'capped' | 'compounding' }>>({
  counts: exactly(true),
  charge: oneOf<'capped' | 'compounding'>(['capped', 'compounding']),
  credit: finiteNumber,
});

const extraNotesOwnError = object<Extract<ExtraNoteRule, { charge: 'own-error' }>>({
  counts: exactly(true),
  charge: exactly('own-error'),
});

/** Told apart by `counts`, then by `charge`. */
const extraNoteRule: Decoder<ExtraNoteRule> = (input, path) => {
  const fields = typeof input === 'object' && input !== null ? input : {};
  if ('counts' in fields && fields.counts === false) {
    return extraNotesNotCounted(input, path);
  }
  return 'charge' in fields && fields.charge === 'own-error'
    ? extraNotesOwnError(input, path)
    : extraNotesCharged(input, path);
};

const matching = object<MatchingConfig>({
  rule: matchingRule,
  regionRules: optional(recordOf(matchingRule)),
  extraNotes: extraNoteRule,
});

const presenceRule = tagged<PresenceRule, 'kind'>('kind', {
  independent: object({ kind: exactly('independent') }),
  all: object({ kind: exactly('all') }),
  'n-of-m': object({ kind: exactly('n-of-m'), n: finiteNumber }),
});

const sliceRule = object<SliceRule>({
  presence: presenceRule,
  sync: optional(creditCurve),
  rolled: optional(object<RolledRule>({ spread: optional(creditCurve), ordered: flag })),
});

const sliceConfig = object<SliceConfig>({
  onset: optional(sliceRule),
  release: optional(sliceRule),
});

const pedalStream = object<PedalStreamConfig>({
  onset: optional(creditCurve),
  offset: optional(creditCurve),
  combiner,
});

export const judgmentConfigDecoder = object<JudgmentConfig>({
  matching,
  note: noteJudgment,
  slices: optional(sliceConfig),
  pedal: optional(pedalStream),
});

const band = object<Band>({ label: text, minCredit: finiteNumber });

const pile = tagged<PileConfig, 'unit'>('unit', {
  note: object({ unit: exactly('note'), includePedal: optional(flag) }),
  slice: object({ unit: exactly('slice'), combiner, includePedal: optional(flag) }),
});

const boundary = tagged<Boundary, 'by'>('by', {
  take: object({ by: exactly('take') }),
  group: object({ by: exactly('group'), key: text }),
  'since-reset': object({ by: exactly('since-reset') }),
});

const scope = tagged<Scope, 'kind'>('kind', {
  rolling: object({
    kind: exactly('rolling'),
    size: finiteNumber,
    per: oneOf<'items' | 'ms'>(['items', 'ms']),
  }),
  bounded: object({
    kind: exactly('bounded'),
    boundary,
    unreached: oneOf<'missed' | 'excluded'>(['missed', 'excluded']),
  }),
});

const recency = tagged<RecencyCurve, 'kind'>('kind', {
  flat: object({ kind: exactly('flat') }),
  rank: object({ kind: exactly('rank') }),
  exponential: object({ kind: exactly('exponential'), halfLife: finiteNumber }),
  points: object({ kind: exactly('points'), weights: listOf(finiteNumber) }),
});

const itemInput = tagged<ItemInput, 'kind'>('kind', {
  credit: object({ kind: exactly('credit') }),
  'credit-lost': object({ kind: exactly('credit-lost') }),
  indicator: object({ kind: exactly('indicator'), atLeast: finiteNumber }),
  'band-weight': object({
    kind: exactly('band-weight'),
    weights: recordOf(finiteNumber),
    bands: optional(listOf(band)),
  }),
  tally: object({ kind: exactly('tally'), bands: optional(listOf(band)) }),
});

const statistic = oneOf<Statistic>(['mean', 'sum', 'minimum', 'longest-run', 'final']);

const combiningLevel = object<CombiningLevel>({
  scope,
  recency: optional(recency),
  input: itemInput,
  statistic,
});

const combining = object<CombiningConfig>({ pile, levels: listOf(combiningLevel) });

const atLine = oneOf<'upper' | 'lower'>(['upper', 'lower']);

const cutLines = object<CutLines>({
  lines: listOf(finiteNumber),
  labels: listOf(text),
  atLine: optional(atLine),
});

const cutLineTest = object<CutLineTest>({
  view: oneOf<VerdictView>(['combined', 'left', 'right', 'each-hand']),
  statistic: optional(statistic),
  lines: listOf(finiteNumber),
  labels: optional(listOf(text)),
});

const comparison = oneOf<Comparison>(['<', '<=', '==', '>=', '>']);

const tallyOperand: Decoder<TallyOperand> = (input, path) => {
  if (typeof input === 'number') {
    return finiteNumber(input, path);
  }
  const byBand = object<{ readonly band: string }>({ band: text })(input, path);
  if (byBand.ok) {
    return byBand;
  }
  const total = object<{ readonly total: true }>({ total: exactly(true) })(input, path);
  return total.ok ? total : failAt(path, 'expected a number, { band } or { total: true }');
};

const comparisonTriple: Decoder<readonly [TallyOperand, Comparison, TallyOperand]> = (
  input,
  path,
) => {
  if (!Array.isArray(input) || input.length !== 3) {
    return failAt(path, 'expected [operand, comparison, operand]');
  }
  const left = tallyOperand(input[0], `${path}[0]`);
  const compared = comparison(input[1], `${path}[1]`);
  const right = tallyOperand(input[2], `${path}[2]`);
  if (left.ok && compared.ok && right.ok) {
    return succeed([left.value, compared.value, right.value] as const);
  }
  return {
    ok: false,
    errors: [left, compared, right].reduce<string[]>(
      (errors, part) => (part.ok ? errors : [...errors, ...part.errors]),
      [],
    ),
  };
};

/** One of `{ compare }`, `{ all }` or `{ any }`; the last two nest. */
const tallyCondition: Decoder<TallyCondition> = (input, path) => {
  const keys = typeof input === 'object' && input !== null ? Object.keys(input) : [];
  const only = keys.length === 1 ? keys[0] : null;
  if (only === 'compare') {
    return object<{ readonly compare: readonly [TallyOperand, Comparison, TallyOperand] }>({
      compare: comparisonTriple,
    })(input, path);
  }
  if (only === 'all') {
    return object<{ readonly all: readonly TallyCondition[] }>({ all: listOf(tallyCondition) })(
      input,
      path,
    );
  }
  if (only === 'any') {
    return object<{ readonly any: readonly TallyCondition[] }>({ any: listOf(tallyCondition) })(
      input,
      path,
    );
  }
  return failAt(path, 'expected exactly one of { compare }, { all } or { any }');
};

const tallyRung = object<TallyRung>({ label: text, when: tallyCondition });

const verdictRule = tagged<VerdictRuleData, 'kind'>('kind', {
  'cut-lines': object({
    kind: exactly('cut-lines'),
    labels: listOf(text),
    tests: listOf(cutLineTest),
    atLine: optional(atLine),
    better: optional(oneOf<'higher' | 'lower'>(['higher', 'lower'])),
  }),
  'tally-ladder': object({
    kind: exactly('tally-ladder'),
    view: oneOf<'combined' | 'left' | 'right'>(['combined', 'left', 'right']),
    rungs: listOf(tallyRung),
    otherwise: text,
  }),
});

const scoring = object<ScoringData>({ combining, verdict: optional(verdictRule) });

const labels = listOf(text);

const accumulatorShape = tagged<AccumulatorShapeData, 'kind'>('kind', {
  'net-counter': object({
    kind: exactly('net-counter'),
    earn: labels,
    lose: labels,
    floor: optional(finiteNumber),
    ceiling: optional(finiteNumber),
  }),
  'count-up': object({ kind: exactly('count-up'), success: labels }),
  streak: object({ kind: exactly('streak'), success: labels }),
  'best-of-window': object({
    kind: exactly('best-of-window'),
    success: labels,
    window: finiteNumber,
  }),
  'personal-best': object({ kind: exactly('personal-best') }),
});

const resetKind = tagged<ResetKind, 'kind'>('kind', {
  'session-boundary': object({ kind: exactly('session-boundary') }),
  'time-decay': object({ kind: exactly('time-decay'), idleMs: finiteNumber }),
  'context-change': object({ kind: exactly('context-change') }),
});

const accumulator = object<AccumulatorData>({
  shape: accumulatorShape,
  target: optional(cutLines),
  resets: listOf(resetKind),
});

// ---------------------------------------------------------------------------------------------
// Decoders for presets and picks.

const advancement = object<Advancement>({
  onMistake: oneOf<OnMistake>(['hold', 'move-on']),
  pace: optional(oneOf<Pace>(['set-tempo', 'follow-me'])),
});

export const presetDefinitionDecoder = object<PresetDefinition>({
  id: text,
  name: text,
  description: text,
  role: oneOf(PRESET_ROLES),
  surfaces: setOf(SURFACES),
  consumers: setOf(CONSUMERS),
  material: oneOf(MATERIAL_SHAPES),
  liveAxes: setOf(LIVE_AXES),
  advancement,
  judgment: judgmentConfigDecoder,
  scoring,
  policy: recordOf(accumulator),
});

export const judgmentPickDecoder = object<JudgmentPick>({
  id: text,
  name: text,
  description: text,
  judgment: judgmentConfigDecoder,
});
